/**
 * Auth context (W-X-02).
 *
 * Holds the current user + role and exposes login/logout. On mount it tries to restore the
 * session via GET /me — the bearer token persisted in localStorage is attached automatically,
 * so a reload stays signed in. On login inside the WebView it notifies the shell (auth.login)
 * so the shell registers the FCM device; logout posts auth.logout and calls the logout endpoint.
 * In a plain browser, logout first tears down web push (lib/push.ts) so this browser stops
 * receiving the signed-out user's notifications.
 */

import { useQueryClient } from "@tanstack/react-query";
import { jwtDecode } from "jwt-decode";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { fetchMe, logoutApi } from "../api/auth";
import { onSessionExpired } from "../api/client";
import { isInWebView, postToShell } from "../bridge/webview";
import { teardownWebPush } from "../lib/push";
import { ACCESS_KEY, clearTokens, setTokens } from "./tokenStore";

/**
 * The sign-in page for whoever is on this URL — used when the session ends before we know the
 * user (e.g. it had already lapsed when the page loaded), so nobody lands on the admin/support
 * landing page by mistake (AUTH-10).
 */
function signInPathForUrl(pathname: string): string {
  if (pathname.startsWith("/patient")) return "/patient";
  if (pathname.startsWith("/staff/doctor") || pathname.startsWith("/doctor")) return "/doctor";
  if (pathname.startsWith("/staff/pharmacist") || pathname.startsWith("/pharmacist")) return "/pharmacist";
  return "/";
}

/** Public pages a visitor may open with an old token in the browser (the account-deletion page):
    a session ending there is dropped quietly, not sent to sign-in. */
const PUBLIC_PREFIXES = ["/legal/", "/delete-account"];
const onPublicPage = (pathname: string) => PUBLIC_PREFIXES.some((p) => pathname.startsWith(p));

/** The user id a bearer token belongs to (its `sub`), or null if it can't be read. */
function tokenUserId(token: string | null): string | null {
  if (!token) return null;
  try {
    return jwtDecode(token).sub ?? null;
  } catch {
    return null;
  }
}

export type Role = "patient" | "doctor" | "pharmacist" | "admin" | "support";

export interface AuthUser {
  id: string;
  role: Role;
  full_name: string;
  must_change_password?: boolean;
}

interface AuthState {
  user: AuthUser | null;
  isAuthenticated: boolean;
  bootstrapping: boolean;
  login: (user: AuthUser, tokens?: { access: string; refresh: string }) => void;
  logout: () => Promise<void>;
  /** The signed-in user saved a new name: show it without signing in again (AUTH-22). */
  renameUser: (full_name: string) => void;
}

const AuthCtx = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [bootstrapping, setBootstrapping] = useState(true);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  // Whose data the query cache currently holds (D-02): one browser may be shared by several users.
  const cacheOwner = useRef<string | null>(null);
  // The signed-in role, kept for the session-expired path (the user is cleared by then).
  const lastRole = useRef<Role | null>(null);

  const adoptUser = useCallback(
    (next: AuthUser | null) => {
      // Never show one user's cached data to another: when a signed-in user leaves (sign-out, or a
      // different user signing in), drop every cached query first.
      const leaving = cacheOwner.current;
      if (leaving !== null && leaving !== (next?.id ?? null)) queryClient.clear();
      cacheOwner.current = next?.id ?? null;
      if (next) lastRole.current = next.role;
      setUser(next);
    },
    [queryClient],
  );

  // Another tab signing out, or signing in as someone else, rewrites the shared token. This tab
  // would then show the old user's cache while sending the new user's token — so reload it, and it
  // bootstraps afresh as whoever is now signed in. A refreshed token for the same user is fine.
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key !== ACCESS_KEY && e.key !== null) return; // null: storage cleared in another tab
      const owner = cacheOwner.current;
      if (!owner || tokenUserId(e.key === null ? null : e.newValue) === owner) return;
      queryClient.clear();
      window.location.reload();
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [queryClient]);

  // The API client ends the session when it was revoked or can't be renewed (XC-03): sign the user
  // out to their own sign-in page with a message, instead of leaving every request failing.
  useEffect(
    () =>
      onSessionExpired(() => {
        if (onPublicPage(window.location.pathname)) {
          adoptUser(null);
          return;
        }
        const role = lastRole.current;
        toast.error("Your session has expired. Please sign in again.");
        adoptUser(null);
        if (isInWebView()) postToShell({ type: "auth.logout" });
        navigate(role ? roleLogin(role) : signInPathForUrl(window.location.pathname), { replace: true });
      }),
    [adoptUser, navigate],
  );

  // Restore the session on first load: the persisted bearer token is sent with /me. With no
  // token (or an expired one) /me returns 401 and we land unauthenticated.
  useEffect(() => {
    let active = true;
    fetchMe()
      .then((me) => {
        if (active)
          adoptUser({
            id: me.id,
            role: me.role,
            full_name: me.full_name,
            must_change_password: me.must_change_password,
          });
      })
      .catch(() => {
        /* not signed in */
      })
      .finally(() => {
        if (active) setBootstrapping(false);
      });
    return () => {
      active = false;
    };
  }, [adoptUser]);

  const login = useCallback(
    (nextUser: AuthUser, tokens?: { access: string; refresh: string }) => {
      adoptUser(nextUser);
      if (tokens) setTokens(tokens.access, tokens.refresh);
      if (isInWebView()) {
        postToShell({
          type: "auth.login",
          user_id: nextUser.id,
          role: nextUser.role,
          access_token: tokens?.access,
          refresh_token: tokens?.refresh,
        });
      }
    },
    [adoptUser],
  );

  const logout = useCallback(async () => {
    // Resolved before the user is cleared: each role lands back on its own sign-in page.
    const signInPath = user ? roleLogin(user.role) : "/";
    // This browser stops getting the user's pushes (ACCT-19): the device is revoked before the
    // session is, so the revoke can still authenticate. The app shell handles its own.
    if (!isInWebView()) await teardownWebPush();
    try {
      await logoutApi("current");
    } catch {
      /* ignore network errors on logout */
    }
    adoptUser(null); // also drops the signed-out user's cached data
    clearTokens();
    if (isInWebView()) postToShell({ type: "auth.logout" });
    navigate(signInPath, { replace: true });
  }, [adoptUser, navigate, user]);

  const renameUser = useCallback((full_name: string) => {
    setUser((u) => (u ? { ...u, full_name } : u));
  }, []);

  const value = useMemo<AuthState>(
    () => ({
      user,
      isAuthenticated: user !== null,
      bootstrapping,
      login,
      logout,
      renameUser,
    }),
    [user, bootstrapping, login, logout, renameUser],
  );

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error("useAuth must be used within <AuthProvider>");
  return ctx;
}

/**
 * Map a role to its home route (project-guidelines.md §2.2). The patient home points past
 * /patient because that URL itself is the patient sign-in page (see roleLogin).
 */
export function roleHome(role: Role): string {
  return role === "patient" ? "/patient/prescriptions" : `/staff/${role}`;
}

/**
 * Map a role to its sign-in page. Patient, doctor and pharmacist sign in at their own URL
 * (/patient, /doctor, /pharmacist), typed directly in the browser — they are not listed on
 * the landing page. Admin and support pick their card on the landing page ("/").
 */
export function roleLogin(role: Role): string {
  return role === "admin" || role === "support" ? "/" : `/${role}`;
}
